import React, { useState } from 'react';
import { ScenarioType } from './ScenarioEditor';
import { Edit2, Check, History, ChevronDown, ChevronUp, Save, Plus } from 'lucide-react';
import { ScenarioData } from '../../interface/ReasearchData';
interface ScenarioSummaryProps {
  scenarios: {
    bull: ScenarioData[];
    base: ScenarioData[];
    bear: ScenarioData[];
  };
  modelName: string;
  activeScenario: ScenarioType;
  onSelectScenario: (scenario: ScenarioType) => void;
  onUpdateModelName: (name: string) => void;
  onSaveScenario: (scenario?: ScenarioType) => void;
  onNewScenario: (scenario?: ScenarioType) => void;
  onOpenHistory: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Calculation base toggle: 'prior' (default) or 'ttm'
  calcBase: 'prior' | 'ttm';
  onChangeCalcBase: (base: 'prior' | 'ttm') => void;
}
export function ScenarioSummary({
  scenarios,
  modelName,
  activeScenario,
  onSelectScenario,
  onUpdateModelName,
  onSaveScenario,
  onNewScenario,
  onOpenHistory,
  isCollapsed = false,
  onToggleCollapse,
  calcBase,
  onChangeCalcBase,
}: ScenarioSummaryProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(modelName);
  const threeYear = new Date().getFullYear() + 2;
  const fiveYear = new Date().getFullYear() + 4;

  const handleSaveName = () => {
    onUpdateModelName(editedName);
    setIsEditingName(false);
  };

  return (
    <div className="bg-theme-secondary rounded-lg border border-theme-tertiary shadow-lg">
      <div className="flex justify-between items-center p-2.5">
        {isEditingName ? (
          <div className="flex items-center">
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="bg-theme-tertiary border border-theme-tertiary rounded px-3 py-1 text-theme-primary text-sm mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={handleSaveName}
              className="p-1 bg-blue-600 rounded-full text-theme-primary hover:bg-blue-700 transition-colors"
            >
              <Check size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center">
            <div className="text-sm font-medium text-theme-primary mr-2">
              {modelName}
            </div>

            <button
              onClick={() => setIsEditingName(true)}
              className="p-1 text-theme-muted hover:text-theme-primary transition-colors"
            >
              <Edit2 size={14} />
            </button>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {/* Calc base radio segmented control (compact) */}
          <div className="flex items-center text-[10px]">
            <span className="text-theme-muted mr-1 whitespace-nowrap">Calc Base:</span>
            <div
              role="radiogroup"
              aria-label="Calculation Base"
              className="inline-flex items-stretch border border-theme-tertiary rounded-md overflow-hidden"
            >
              <label
                className={`px-1.5 py-0.5 cursor-pointer select-none ${
                  calcBase === 'prior'
                    ? 'active-bg'
                    : ''
                }`}
              >
                <input
                  type="radio"
                  name="calcBase"
                  className="sr-only"
                  checked={calcBase === 'prior'}
                  onChange={() => onChangeCalcBase('prior')}
                />
                Prior
              </label>
              <label
                className={`px-1.5 py-0.5 cursor-pointer select-none border-l border-theme-tertiary ${
                  calcBase === 'ttm'
                    ? 'active-bg'
                    : ''
                }`}
              >
                <input
                  type="radio"
                  name="calcBase"
                  className="sr-only"
                  checked={calcBase === 'ttm'}
                  onChange={() => onChangeCalcBase('ttm')}
                />
                TTM
              </label>
            </div>
          </div>
          <button
            className={`flex px-3 py-1 text-xs justify-center items-center btn btn-sm text-green-500 hover:text-green-400 transition-colors border border-theme-tertiary rounded`}
            onClick={(e) => {
              e.stopPropagation();
              onSaveScenario(activeScenario);
            }}
          >
            <Save size={14} />&nbsp;Save
          </button>
          <button
            className={`flex p-1.5  justify-center items-center btn btn-sm rounded-full text-green-500 hover:text-green-400 transition-colors border border-theme-tertiary`}
            onClick={(e) => {
              e.stopPropagation();
              onNewScenario(activeScenario);
            }}
          >
            <Plus size={14} />
          </button>
          <button
            onClick={onOpenHistory}
            className="flex items-center text-xs btn btn-sm text-theme-secondary hover:text-theme-primary px-2 py-1 rounded transition-colors border border-theme-tertiary"
          >
            <History size={12} className="mr-1" />
            Recent Scenarios
          </button>

          <button
            onClick={onToggleCollapse}
            className="p-1 bg-theme-tertiary rounded-full text-theme-muted hover:text-theme-primary hover:bg-theme-tertiary/70 transition-colors border border-theme-tertiary"
          >
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>
      {!isCollapsed && (
        <div className="grid grid-cols-3 gap-6 p-3 border-t border-theme-tertiary">
          <ScenarioSummaryCard
            threeYear={threeYear.toString()}
            fiveYear={fiveYear.toString()}
            title="Bull Case"
            color="green"
            isActive={activeScenario === 'bull'}
            threeYearPrice={{
              low: scenarios.bull?.thirdYearProjectionSharePriceLow || '0',
              high: scenarios.bull?.thirdYearProjectionSharePriceHigh || '0',
            }}
            fiveYearPrice={{
              low: scenarios.bull?.fifthYearProjectionSharePriceLow || '0',
              high: scenarios.bull?.fifthYearProjectionSharePriceHigh || '0',
            }}
            threeYearCAGR={scenarios?.bull.thirdYearProjectionCGR}
            fiveYearCAGR={scenarios?.bull.fifthYearProjectionCGR}
            onClick={() => onSelectScenario('bull')}
          />
          <ScenarioSummaryCard
            threeYear={threeYear.toString()}
            fiveYear={fiveYear.toString()}
            title="Base Case"
            color="blue"
            isActive={activeScenario === 'base'}
            threeYearPrice={{
              low: scenarios.base?.thirdYearProjectionSharePriceLow || '0',
              high: scenarios.base?.thirdYearProjectionSharePriceHigh || '0',
            }}
            fiveYearPrice={{
              low: scenarios.base?.fifthYearProjectionSharePriceLow || '0',
              high: scenarios.base?.fifthYearProjectionSharePriceHigh || '0',
            }}
            threeYearCAGR={scenarios?.base.thirdYearProjectionCGR}
            fiveYearCAGR={scenarios?.base.fifthYearProjectionCGR}
            onClick={() => onSelectScenario('base')}
          />
          <ScenarioSummaryCard
            threeYear={threeYear.toString()}
            fiveYear={fiveYear.toString()}
            title="Bear Case"
            color="red"
            isActive={activeScenario === 'bear'}
            threeYearPrice={{
              low: scenarios.bear?.thirdYearProjectionSharePriceLow || '0',
              high: scenarios.bear?.thirdYearProjectionSharePriceHigh || '0',
            }}
            fiveYearPrice={{
              low: scenarios.bear?.fifthYearProjectionSharePriceLow || '0',
              high: scenarios.bear?.fifthYearProjectionSharePriceHigh || '0',
            }}
            threeYearCAGR={scenarios?.bear.thirdYearProjectionCGR}
            fiveYearCAGR={scenarios?.bear.fifthYearProjectionCGR}
            onClick={() => onSelectScenario('bear')}
          />
        </div>
      )}
    </div>
  );
}
interface ScenarioSummaryCardProps {
  title: string;
  color: string;
  isActive: boolean;
  threeYear: string;
  fiveYear: string;
  threeYearPrice: {
    low: string;
    high: string;
  };
  fiveYearPrice: {
    low: string;
    high: string;
  };
  threeYearCAGR: string;
  fiveYearCAGR: string;
  onClick: () => void;
  // onSave: () => void;
}
function ScenarioSummaryCard({
  title,
  color,
  isActive,
  threeYear,
  fiveYear,
  threeYearPrice,
  fiveYearPrice,
  threeYearCAGR,
  fiveYearCAGR,
  onClick,
  // onSave
}: ScenarioSummaryCardProps) {
  return <div className={`
        rounded-lg p-3 border transition-all cursor-pointer relative bg-theme-tertiary/30
        ${isActive ? `border-${color}-500/50 ring-1 ring-${color}-500/30 shadow-lg` : `border-theme-tertiary hover:border-${color}-500/30`}
      `} onClick={onClick}>

    <div className={`text-${color}-500 font-medium mb-3`}>{title}</div>
    <div className="mb-3">
      <div className="text-xs text-theme-muted mb-1">
        3-Year Projection {threeYear}
      </div>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs text-theme-muted">Price Range</div>
          <div className={`text-sm text-${color}-500 font-medium`}>
            {threeYearPrice.low} - {threeYearPrice.high}
          </div>
        </div>
        <div>
          <div className="text-xs text-theme-muted">CAGR</div>
          <div className={`text-sm text-${color}-500 font-medium`}>
            {threeYearCAGR}
          </div>
        </div>
      </div>
    </div>
    <div className="pt-2 border-t border-theme-tertiary/30">
      <div className="text-xs text-theme-muted mb-1">
        5-Year Projection {fiveYear}
      </div>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs text-theme-muted">Price Range</div>
          <div className={`text-sm text-${color}-500 font-medium`}>
            {fiveYearPrice.low} - {fiveYearPrice.high}
          </div>
        </div>
        <div>
          <div className="text-xs text-theme-muted">CAGR</div>
          <div className={`text-sm text-${color}-500 font-medium`}>
            {fiveYearCAGR}
          </div>
        </div>
      </div>
    </div>
  </div>;
}
function calculateCAGR(data: ScenarioData[], years: number) {
  // const startYear = '2025';
  // const endYear = years === 3 ? '2027' : '2029';
  // const startValue = 100; // Base value
  // const endValue = parseFloat(data.find(d => d.fiscalYear === endYear)?.sharePriceHigh || '0');
  // if (endValue <= 0) return '0.00';
  // const cagr = ((Math.pow(endValue / startValue, 1 / years) - 1) * 100).toFixed(2);
  // return cagr;
  return 0;
}
function formatLargeNumber(value: string): string {
  const num = parseFloat(value.replace(/,/g, ''));
  if (isNaN(num)) return '0';
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  } else {
    return num.toLocaleString();
  }
}